/**
 * Resize an image File before upload so we don't blow past the Supabase
 * Edge Function payload cap (~6 MB) on big phone photos.
 *
 * - Pass-through if already <= maxBytes (default 1.8 MB).
 * - Otherwise downscales the longest edge to maxEdge (default 1920 px) and
 *   re-encodes as JPEG at the requested quality.
 *
 * Returns a new File preserving the original filename (with .jpg if the
 * source was not already JPEG).
 */
export async function resizeImage(
  file: File,
  opts: { maxBytes?: number; maxEdge?: number; quality?: number } = {},
): Promise<File> {
  const maxBytes = opts.maxBytes ?? 1_800_000;
  const maxEdge = opts.maxEdge ?? 1920;
  const quality = opts.quality ?? 0.85;

  if (!file.type.startsWith('image/')) return file;
  if (file.size <= maxBytes) return file;

  const dataUrl = await readAsDataUrl(file);
  const img = await loadImage(dataUrl);

  const longest = Math.max(img.width, img.height);
  const scale = longest > maxEdge ? maxEdge / longest : 1;
  const targetWidth = Math.round(img.width * scale);
  const targetHeight = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
  if (!blob) return file;

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'receipt';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = src;
  });
}
